import api from '../api'

// ─── Candidates ───

export const fetchCandidateList = async ({
  search,
  page,
  pageSize,
  filters,
}: {
  search: string
  page: number
  pageSize: number
  filters: { [key: string]: string[] }
}) => {
  const params: Record<string, any> = {
    search,
    page,
    page_size: pageSize,
    expand: 'vacancy',
    fields: '*,vacancy.id,vacancy.title,vacancy.country',
  }
  Object.entries(filters).forEach(([key, values]) => {
    if (values.length > 1) {
      params[`${key}__in`] = values.join(',')
    } else if (values.length === 1) {
      params[key] = values[0]
    }
  })
  const response = await api.get('/recruitment/candidates/', { params })
  return response.data
}

export const fetchCandidate = async (candidateId: string) => {
  const response = await api.get(`/recruitment/candidates/${candidateId}/`, {
    params: {
      expand: 'vacancy',
      fields: '*,vacancy.id,vacancy.title,vacancy.country',
    },
  })
  return response.data
}
export const createCandidate = async (candidate: FormData) => {
  const response = await api.post('/recruitment/candidates/', candidate)
  return response.data
}

export const fetchJobProfiles = async () => {
  const response = await api.get('/classifications/job-profiles/', {
    params: {
      fields: 'id,title',
    },
  })
  return response.data
}

export const updateCandidate = async (candidate: any) => {
  const response = await api.patch(`/recruitment/candidates/${candidate.id}/`, candidate)
  return response.data
}

export const deleteCandidate = async (candidateId: string) => {
  const response = await api.delete(`/recruitment/candidates/${candidateId}/`)
  return response.data
}

// ─── Applications ───

export const fetchApplicationList = async ({
  search,
  page,
  pageSize,
  filters,
}: {
  search: string
  page: number
  pageSize: number
  filters: { [key: string]: string[] }
}) => {
  const params: Record<string, any> = {
    search,
    page,
    page_size: pageSize,
    expand: 'candidate,vacancy',
    fields: '*,candidate.id,candidate.first_name,candidate.last_name,candidate.email,candidate.source,vacancy.id,vacancy.title',
  }
  Object.entries(filters).forEach(([key, values]) => {
    if (values.length > 1) {
      params[`${key}__in`] = values.join(',')
    } else if (values.length === 1) {
      params[key] = values[0]
    }
  })
  const response = await api.get('/recruitment/applications/', { params })
  return response.data
}

export const fetchApplication = async (applicationId: string) => {
  const response = await api.get(`/recruitment/applications/${applicationId}/`, {
    params: {
      expand: 'candidate,vacancy',
    },
  })
  return response.data
}

export const createApplication = async (application: any) => {
  const response = await api.post('/recruitment/applications/', application)
  return response.data
}

export const updateApplication = async (application: any) => {
  const response = await api.patch(`/recruitment/applications/${application.id}/`, application)
  return response.data
}

export const deleteApplication = async (applicationId: string) => {
  const response = await api.delete(`/recruitment/applications/${applicationId}/`)
  return response.data
}

// ─── Interviews ───

export const fetchInterviewList = async ({
  search,
  page,
  pageSize,
  filters,
}: {
  search: string
  page: number
  pageSize: number
  filters: { [key: string]: string[] }
}) => {
  const params: Record<string, any> = {
    search,
    page,
    page_size: pageSize,
    expand: 'application,application.candidate,interviewer',
    fields: '*,application.id,application.candidate.id,application.candidate.first_name,application.candidate.last_name,interviewer.id,interviewer.first_name,interviewer.last_name,interviewer.profile_picture',
  }
  Object.entries(filters).forEach(([key, values]) => {
    if (values.length > 1) {
      params[`${key}__in`] = values.join(',')
    } else if (values.length === 1) {
      params[key] = values[0]
    }
  })
  const response = await api.get('/recruitment/interviews/', { params })
  return response.data
}

export const fetchInterview = async (interviewId: string) => {
  const response = await api.get(`/recruitment/interviews/${interviewId}/`, {
    params: {
      expand: 'application,application.candidate,interviewer',
    },
  })
  return response.data
}

export const createInterview = async (interview: any) => {
  const response = await api.post('/recruitment/interviews/', interview)
  return response.data
}

export const updateInterview = async (interview: any) => {
  const response = await api.patch(`/recruitment/interviews/${interview.id}/`, interview)
  return response.data
}

export const deleteInterview = async (interviewId: string) => {
  const response = await api.delete(`/recruitment/interviews/${interviewId}/`)
  return response.data
}

// ─── Reports ───

export const fetchRecruitmentReports = async () => {
  const response = await api.get('/recruitment/reports/')
  return response.data
}
export const fetchApplicationBreakdown = async (period: string) => {
  const response = await api.get('/recruitment/reports/', {
    params: { period }
  })
  return response.data
}

export const fetchScreeningApplications = async (cursor?: string | null) => {
  const params: Record<string, any> = {}

  if (cursor) {
    params.cursor = cursor
  }

  const response = await api.get('/recruitment/screening-applications/', { params })
  return response.data
}

// ─── Vacancies ───

export const fetchVacancyList = async ({
  search,
  page,
  pageSize,
  filters,
}: {
  search: string
  page: number
  pageSize: number
  filters: { [key: string]: string[] }
}) => {
  const params: Record<string, any> = {
    search,
    page,
    page_size: pageSize,
    expand: 'job_profile',
    fields: '*,job_profile.id,job_profile.title',
  }

  Object.entries(filters).forEach(([key, values]) => {
    if (values.length > 1) {
      params[`${key}__in`] = values.join(',')
    } else if (values.length === 1) {
      params[key] = values[0]
    }
  })

  const response = await api.get('/recruitment/vacancies/', { params })
  return response.data
}

export const fetchVacancy = async (vacancyId: string) => {
  const response = await api.get(`/recruitment/vacancies/${vacancyId}/`, {
    params: {
      expand: 'job_profile',
      fields: '*,job_profile.id,job_profile.title',
    },
  })
  return response.data
}

export const createVacancy = async (vacancy: {
  title: string
  country: string
  description: string
  job_profile: string
}) => {
  const response = await api.post('/recruitment/vacancies/', vacancy)
  return response.data
}

export const updateVacancy = async (vacancy: {
  id: string | number
  title?: string
  country?: string
  description?: string
  job_profile?: string
}) => {
  const response = await api.patch(`/recruitment/vacancies/${vacancy.id}/`, vacancy)
  return response.data
}

export const deleteVacancy = async (vacancyId: string) => {
  const response = await api.delete(`/recruitment/vacancies/${vacancyId}/`)
  return response.data
}

export const fetchVacancyId = async (vacancyId: string) => {
  const response = await api.get(`/recruitment/vacancies/${vacancyId}/`, {
    params: {
      expand: 'job_profile',
      fields: '*,job_profile.id,job_profile.title',
    },
  })
  return response.data
}

// ─── Vacancy Overview ───

export const fetchVacancyOverview = async () => {
  const response = await api.get('/recruitment/vacancy-overview/')
  return response.data
}

// ─── Inflow ───

export async function fetchInflowCandidates(params?: {
  page?: number
  page_size?: number
  search?: string
}) {
  const res = await api.get('/recruitment/inflow/', { params })
  return res.data
}

export async function patchInflowCandidate(
  id: string,
  data: Partial<{
    it_access: boolean
    keys: boolean
    devices: boolean
    other_assets: boolean
    welcome_meeting_planned: boolean
    employee: string
  }>,
) {
  const res = await api.patch(`/recruitment/inflow/${id}/`, data)
  return res.data
}
